class OwBundleValue extends HTMLElement {
  connectedCallback() {
    this.options = this.#parseJson('[data-bundle-options]') || [];
    this.variants = this.#parseJson('[data-bundle-variants]') || [];
    this.productId = String(this.dataset.productId || '');
    this.currency = this.dataset.currency || 'USD';
    this.productTitle = this.dataset.productTitle || '';
    this.fixedPackCount = this.#packCount(this.productTitle, false);
    this.packIndex = this.#findPackIndex();
    this.onVariantUpdate = this.#onVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.onVariantUpdate);
    this.#render(this.dataset.currentVariant);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.onVariantUpdate);
  }

  #parseJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null');
    } catch (_error) {
      return null;
    }
  }

  #findPackIndex() {
    const namedIndex = this.options.findIndex((name) =>
      /pack|quantity|count|bundle|set|paquete|cantidad|lote|包装|包裝|套装|套裝|数量|數量|件数|件數/i.test(String(name))
    );
    if (namedIndex >= 0) return namedIndex;

    return this.options.findIndex((_name, index) =>
      this.variants.some((variant) => this.#packCount(variant.options?.[index], false) > 1)
    );
  }

  #packCount(value, strict = true) {
    const normalized = String(value || '').trim().toLowerCase();
    if (
      /^(single|each|one|individual|unidad|uno|una|1[- ]?(pack|plant|count)|单株|單株|单个|單個|一株|1株)$/.test(normalized)
    ) return 1;

    const leading = normalized.match(/^(\d+)\s*[- ]?\s*(pack|pk|count|plants?|pieces?|pcs?)\b/);
    if (leading) return Number(leading[1]);

    const setOf = normalized.match(/(?:pack|set)\s+of\s+(\d+)/);
    if (setOf) return Number(setOf[1]);

    const spanish = normalized.match(/(?:paquete|pack|juego|lote)\s+(?:de\s+)?(\d+)/)
      || normalized.match(/^(\d+)\s*(?:unidades?|plantas?|piezas?)\b/);
    if (spanish) return Number(spanish[1]);

    const chinese = normalized.match(/^(\d+)\s*(?:株|件|个|個|入)\s*(?:装|裝|组|組)?$/);
    if (chinese) return Number(chinese[1]);

    if (/^(?:两|兩)株(?:装|裝)?$/.test(normalized)) return 2;

    if (/\bduo\b|\btwo\s+(?:live\s+)?(?:plants?|houseplants?|trees?)\b/.test(normalized)) return 2;
    if (/\btrio\b|\bthree\s+(?:live\s+)?(?:plants?|houseplants?|trees?)\b/.test(normalized)) return 3;
    if (/\bfour\s+(?:assorted\s+)?(?:live\s+)?(?:plants?|houseplants?|trees?)\b/.test(normalized)) return 4;
    if (/romeo\s+and\s+juliet\s+bundle/.test(normalized)) return 2;

    if (!strict && /^\d+$/.test(normalized)) return Number(normalized);
    return null;
  }

  #variantPackCount(variant) {
    if (!variant) return null;
    if (this.packIndex >= 0) {
      return this.#packCount(variant.options?.[this.packIndex], false) || 1;
    }
    return this.fixedPackCount;
  }

  #sameConfiguration(first, second) {
    if (this.packIndex < 0) return true;
    return first.options.every((value, index) => index === this.packIndex || value === second.options[index]);
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency,
    }).format(cents / 100);
  }

  #fill(template, values) {
    return Object.entries(values).reduce(
      (copy, [key, value]) => copy.replaceAll(`{{ ${key} }}`, String(value)),
      template || ''
    );
  }

  #badgeTemplate() {
    const language = String(document.documentElement.lang || 'en').toLowerCase();
    if (language.startsWith('es')) return 'Ahorra {{ percent }}%';
    if (language.startsWith('zh')) return '省 {{ percent }}%';
    return 'Save {{ percent }}%';
  }

  #onVariantUpdate(event) {
    if (String(event.detail?.data?.productId || '') !== this.productId) return;
    this.#render(event.detail?.resource?.id);
  }

  #decoratePackOptions(comparable) {
    if (this.packIndex < 0) return;
    const picker = document.querySelector(`variant-picker[data-product-id="${this.productId}"]`);
    const fieldset = picker?.querySelector(`[data-fieldset-index="${this.packIndex}"]`);
    if (!fieldset) return;

    fieldset.querySelectorAll('.ow-bundle-badge').forEach((badge) => badge.remove());
    const single = comparable.find(
      (variant) => this.#variantPackCount(variant) === 1
    );
    if (!single) return;

    fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const count = this.#packCount(input.value, false);
      if (!count || count <= 1) return;

      const pack = comparable.find(
        (variant) => this.#variantPackCount(variant) === count
      );
      if (!pack) return;

      const fullSinglePrice = single.price * count;
      const savings = fullSinglePrice - pack.price;
      if (savings <= 0) return;

      const badge = document.createElement('span');
      badge.className = 'ow-bundle-badge';
      badge.textContent = this.#fill(this.#badgeTemplate(), {
        percent: Math.round((savings / fullSinglePrice) * 100),
      });
      input.closest('label')?.appendChild(badge);
    });
  }

  #render(variantId) {
    this.hidden = true;
    if (this.packIndex < 0 && !this.fixedPackCount) return;

    const selected = this.variants.find((variant) => String(variant.id) === String(variantId));
    if (!selected) return;

    const selectedCount = this.#variantPackCount(selected);
    if (!selectedCount) return;

    const comparable = this.variants.filter(
      (variant) => variant.available && this.#sameConfiguration(selected, variant)
    );
    this.#decoratePackOptions(comparable);
    const title = this.querySelector('[data-bundle-title]');
    const copy = this.querySelector('[data-bundle-copy]');
    const icon = this.querySelector('[data-bundle-icon]');
    if (!title || !copy || !icon) return;

    title.textContent = this.dataset.title || 'Bundle value';

    if (selectedCount > 1) {
      const single = comparable.find(
        (variant) => this.#variantPackCount(variant) === 1
      );
      const unitPrice = Math.round(selected.price / selectedCount);

      if (single) {
        const savings = single.price * selectedCount - selected.price;
        const percent = savings > 0 ? Math.round((savings / (single.price * selectedCount)) * 100) : 0;
        if (savings > 0) {
          icon.textContent = `${percent}%`;
          copy.textContent = this.#fill(this.dataset.saveTemplate, {
            unit_price: this.#money(unitPrice),
            savings: this.#money(savings),
            percent,
          });
          this.hidden = false;
          return;
        }
      }

      icon.textContent = `${selectedCount}×`;
      copy.textContent = this.#fill(this.dataset.perPlant, { price: this.#money(unitPrice) });
      this.hidden = false;
      return;
    }

    const multiPack = comparable
      .filter((variant) => this.#variantPackCount(variant) > 1)
      .sort(
        (first, second) =>
          this.#variantPackCount(first) - this.#variantPackCount(second)
      )
      .find((variant) => {
        const count = this.#variantPackCount(variant);
        return selected.price * count > variant.price;
      });

    if (!multiPack) return;
    const count = this.#variantPackCount(multiPack);
    const savings = selected.price * count - multiPack.price;
    icon.textContent = `${count}×`;
    copy.textContent = this.#fill(this.dataset.chooseTemplate, {
      pack: multiPack.options[this.packIndex],
      unit_price: this.#money(Math.round(multiPack.price / count)),
      savings: this.#money(savings),
    });
    this.hidden = false;
  }
}

if (!customElements.get('ow-bundle-value')) {
  customElements.define('ow-bundle-value', OwBundleValue);
}
